import { AirplaneTilt, Coins, Compass, ListChecks, Plus, ShieldCheck } from '@phosphor-icons/react';

import { Button } from '../ui/Button';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { allocationDotClass } from '../../utils/quickCreateAllocation';
import { GoalTermHint } from './GoalTermHint';

const EXAMPLE_ICONS = {
  'shield-check': ShieldCheck,
  'airplane-tilt': AirplaneTilt,
};

function SectionHeading({ label, hint }) {
  return (
    <div className="flex items-center gap-space-2">
      <p className="type-section text-muted-foreground">{label}</p>
      <p className="type-chip text-muted-foreground">{hint}</p>
    </div>
  );
}

function ExampleSuggestionCard({ example, onSelect }) {
  const Icon = EXAMPLE_ICONS[example.icon] || ShieldCheck;
  return (
    <button
      type="button"
      onClick={() => onSelect(example)}
      className="flex w-full items-center gap-space-3 rounded-ds-md border border-border bg-card p-space-4 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
    >
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-ds-md text-on-hero',
          allocationDotClass(example.budget_allocation),
        )}
      >
        <Icon className="h-[22px] w-[22px]" aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="type-body font-semibold text-card-foreground">{example.name}</span>
        <span className="type-body-sm text-muted-foreground">{example.description}</span>
      </span>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-ds-full bg-sage-tint text-sage-on-tint">
        <Plus className="h-3.5 w-3.5" weight="bold" aria-hidden />
      </span>
    </button>
  );
}

function TypeHintRow({ icon: Icon, term, label, body }) {
  return (
    <div className="flex flex-col gap-space-2 rounded-ds-md bg-muted p-space-4">
      <div className="flex items-center gap-space-2">
        <Icon className="h-4 w-4 text-sage-on-tint" aria-hidden />
        <p className="type-label-lg font-semibold text-card-foreground">{label}</p>
        <GoalTermHint term={term} variant="icon" />
      </div>
      <p className="type-body-sm text-muted-foreground">{body}</p>
    </div>
  );
}

export function GoalsEmptyVision({ onCreate, onExampleSelect, className }) {
  const examples = pt.goals.vision.examples;
  const empty = pt.goals.vision.empty;

  return (
    <div className={cn('flex flex-col gap-space-5', className)}>
      <div className="flex flex-col items-center gap-space-4 rounded-ds-xl bg-sage-solid p-space-6 text-center shadow-[0_8px_24px_theme(colors.sage.glow)] md:p-space-8 lg:p-space-10">
        <span className="flex h-14 w-14 items-center justify-center rounded-ds-full bg-on-hero-track">
          <Compass className="h-[26px] w-[26px] text-on-hero" aria-hidden />
        </span>
        <h2 className="type-title-page-lg text-balance text-on-hero">{empty.heroTitle}</h2>
        <p className="type-body-sm max-w-md text-on-hero-muted">{empty.heroBody}</p>
        <p className="type-label w-full rounded-ds-md bg-on-hero-track p-space-3 text-on-hero">
          {empty.smartNote}
        </p>
        <Button
          type="button"
          size="lg"
          className="w-full bg-on-hero text-sage-700 hover:bg-on-hero/90"
          onClick={onCreate}
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden />
          {empty.cta}
        </Button>
      </div>

      <div className="flex flex-col gap-space-5 md:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-space-3">
          <SectionHeading label={empty.startHereLabel} hint={empty.startHereHint} />
          {examples.map((example) => (
            <ExampleSuggestionCard key={example.name} example={example} onSelect={onExampleSelect} />
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-space-3">
          <SectionHeading label={empty.typesLabel} hint={empty.typesHint} />
          <TypeHintRow
            icon={Coins}
            term="monetary"
            label={empty.typeMonetaryLabel}
            body={empty.typeMonetaryBody}
          />
          <TypeHintRow
            icon={ListChecks}
            term="checklist"
            label={empty.typeChecklistLabel}
            body={empty.typeChecklistBody}
          />
        </div>
      </div>
    </div>
  );
}
